
import React, { useEffect, useState, useCallback } from "react";
import "./Navbar.css";
import { FaBars, FaTimes } from "react-icons/fa";
import { NavLink, useLocation } from "react-router-dom";

const NAVALUR_LOCATION_URL =
  "https://www.thehexaplay.com/locations/navalur/";

const MOBILE_BREAKPOINT = 1024;

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();

  const isMobile = () =>
    window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;

  /* ================= SCROLL EFFECT ================= */

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* ================= CLOSE MENU ================= */

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, []);

  /* ================= BODY SCROLL LOCK ================= */

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;

    // Prevent background scrolling while drawer is open.
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    const handleResize = () => {
      if (window.innerWidth > MOBILE_BREAKPOINT) {
        closeMenu();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;

      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
    };
  }, [open, closeMenu]);

  /* ================= ROUTE CHANGE ================= */

  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  /* ================= CALENDAR EVENT ================= */

  useEffect(() => {
    window.addEventListener("hexaplay:calendar-open", closeMenu);

    return () => {
      window.removeEventListener(
        "hexaplay:calendar-open",
        closeMenu
      );
    };
  }, [closeMenu]);

  /* ================= MENU TOGGLE ================= */

  const toggleMenu = () => {
    if (!isMobile()) return;

    setOpen((previous) => !previous);
  };

  /* ================= NAVIGATION ================= */

  const navLinkClass = ({ isActive }) =>
    `nav-link ${isActive ? "active" : ""}`;

  const navItems = [
    {
      path: "/",
      label: "Home",
      end: true,
    },
    {
      path: "/AboutUs",
      label: "About",
    },
    {
      path: "/Services",
      label: "Services",
    },
    {
      path: "/Gallery",
      label: "Gallery",
    },
    {
      href: NAVALUR_LOCATION_URL,
      label: "Location",
      external: true,
    },
    {
      path: "/EventServices",
      label: "Outdoor Events",
    },
    {
      path: "/Gaming",
      label: "Gaming",
    },
    {
      path: "/Contact",
      label: "Contact",
    },
  ];

  return (
    <>
      {/* ================= NAVBAR ================= */}

      <header className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="nav-container">

          {/* LOGO */}

          <NavLink
            to="/"
            className="logo"
            onClick={closeMenu}
          >
            <img
              src="/images/logo11.png"
              alt="Hexaplay Logo"
              className="logo-img"
            />

            <div className="logo-text">
              <span className="logo-title">
                The Hexaplay
              </span>

              <span className="logo-sub">
                Private Theatre • Gaming
              </span>
            </div>
          </NavLink>

          {/* NAVIGATION */}

          <nav
            id="mobile-navigation"
            className={`nav-menu ${open ? "active" : ""}`}
            aria-label="Main navigation"
            aria-hidden={isMobile() && !open}
          >

            {/* MOBILE HEADER */}

            <div className="mobile-menu-header">

              <NavLink
                to="/"
                className="logo"
                onClick={closeMenu}
                tabIndex={open ? 0 : -1}
              >
                <img
                  src="/images/logo11.png"
                  alt="Hexaplay Logo"
                  className="logo-img"
                />

                <div className="logo-text">
                  <span className="logo-title">
                    The Hexaplay
                  </span>

                  <span className="logo-sub">
                    Private Theatre • Gaming
                  </span>
                </div>
              </NavLink>

              <button
                className="mobile-close"
                onClick={closeMenu}
                type="button"
                aria-label="Close navigation menu"
                tabIndex={open ? 0 : -1}
              >
                <FaTimes aria-hidden="true" />
              </button>

            </div>

            {/* NAV LINKS */}

            <div className="nav-links-wrapper">

              {navItems.map((item) =>
                item.external ? (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_self"
                    className={`nav-link ${
                      location.pathname === "/locations/navalur/"
                        ? "active"
                        : ""
                    }`}
                    onClick={closeMenu}
                    tabIndex={!isMobile() || open ? 0 : -1}
                  >
                    {item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.end}
                    className={navLinkClass}
                    onClick={closeMenu}
                    tabIndex={!isMobile() || open ? 0 : -1}
                  >
                    {item.label}
                  </NavLink>
                )
              )}

            </div>

            {/* MOBILE BOOK BUTTON */}

            <div className="mobile-book-wrapper">

              <NavLink
                to="/"
                onClick={closeMenu}
                tabIndex={open ? 0 : -1}
              >
                <span className="btn-book mobile-btn">
                  Book Now
                </span>
              </NavLink>

            </div>

          </nav>

          {/* RIGHT AREA */}

          <div className="nav-right">

            <NavLink
              to="/"
              className="desktop-book-link"
            >
              <span className="btn-book desktop-btn">
                Book Now
              </span>
            </NavLink>

            {/* HAMBURGER BUTTON */}

            <button
              className={`menu-btn ${open ? "is-open" : ""}`}
              onClick={toggleMenu}
              type="button"
              aria-label={
                open
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              aria-controls="mobile-navigation"
              aria-expanded={open}
            >
              {open ? (
                <FaTimes aria-hidden="true" />
              ) : (
                <FaBars aria-hidden="true" />
              )}
            </button>

          </div>

        </div>
      </header>

      {/* ================= BACKDROP ================= */}

      <div
        className={`backdrop ${open ? "active" : ""}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

    </>
  );
};

export default Navbar;
